<template>
  <div class="qr-payment-settings">
    <h4>{{ $t('settingTable.qrPaymentMethods') }}</h4>
    <div class="row">
      <div v-for="mode in modes" :key="mode" class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">{{ $t('settingTable.qrPaymentMode_' + mode) }}</h4>
          </div>
          <div class="card-content">
            <div class="form-group">
              <p-checkbox :checked="isSelected(mode, 'CASH')"
                          :name="'qr_payment_' + mode + '_CASH'"
                          @input="toggleMethod(mode, 'CASH', $event)">
                {{ $t('settingTable.qrPaymentType_CASH') }}
              </p-checkbox>
            </div>
            <div class="form-group">
              <p-checkbox :checked="isSelected(mode, 'POS')"
                          :name="'qr_payment_' + mode + '_POS'"
                          @input="toggleMethod(mode, 'POS', $event)">
                {{ $t('settingTable.qrPaymentCard') }}
              </p-checkbox>
            </div>
            <div class="form-group">
              <p-checkbox :checked="isMobileSelected(mode)"
                          :name="'qr_payment_' + mode + '_mobile'"
                          @input="toggleMobile(mode, $event)">
                {{ $t('settingTable.qrPaymentMobilePos') }}
              </p-checkbox>
            </div>
            <div v-if="isMobileSelected(mode)" class="qr-mobile-payment-options">
              <div class="form-group">
                <p-radio :value="mobileConnection(mode)" label="NFC"
                         @input="setMobileConnection(mode, $event)">NFC</p-radio>
              </div>
              <div v-if="mobileConnection(mode) === 'NFC'" class="form-group">
                <label>{{ $t('settingTable.qrPaymentBankTerminal') }}</label>
                <select class="form-control"
                        :name="'qr_payment_' + mode + '_nfc_provider'"
                        :value="nfcProvider(mode) || ''"
                        @change="setNfcProvider(mode, $event.target.value)">
                  <option value="">{{ $t('general.select') }}</option>
                  <option value="viva_wallet">Viva Wallet</option>
                  <option value="banca_transilvania">BT</option>
                </select>
                <small v-for="(message, index) in fieldErrors(mode, 'bank_terminal_provider')" :key="index" class="text-danger">{{ message }}</small>
              </div>
              <div class="form-group">
                <p-radio :value="mobileConnection(mode)" label="DIRECT"
                         @input="setMobileConnection(mode, $event)">
                  {{ $t('settingTable.qrPaymentDirectConnection') }}
                </p-radio>
              </div>
              <template v-if="mobileConnection(mode) === 'DIRECT'">
                <div class="form-group">
                  <label>{{ $t('settingTable.type') }}</label>
                  <select class="form-control"
                          :value="terminalData(mode, 'CARD_POS').integrated_bank_pos_id || ''"
                          @change="setTerminalValue(mode, 'integrated_bank_pos_id', $event.target.value, 'CARD_POS')">
                    <option value="">{{ $t('general.select') }}</option>
                    <option v-for="option in posOptions" :key="option.id" :value="option.id">{{ option.name }}</option>
                  </select>
                  <small v-for="(message, index) in fieldErrors(mode, 'integrated_bank_pos_id')" :key="index" class="text-danger">{{ message }}</small>
                </div>
                <div v-for="field in terminalFields" :key="field.key" class="form-group">
                  <label>{{ field.label === 'TID/MID' ? field.label : $t(field.label) }}</label>
                  <input type="text" class="form-control" maxlength="255"
                         :value="terminalData(mode, 'CARD_POS')[field.key]"
                         @input="setTerminalValue(mode, field.key, $event.target.value, 'CARD_POS')">
                  <small v-for="(message, index) in fieldErrors(mode, field.key)" :key="index" class="text-danger">{{ message }}</small>
                </div>
              </template>
            </div>
            <p v-for="(message, index) in modeErrors(mode)" :key="'mode-' + index" class="text-danger">{{ message }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: Object, default: null },
    posOptions: { type: Array, required: true },
    errorBag: { type: Object, default: null },
    legacyBankProvider: { type: String, default: null }
  },
  data () {
    return {
      modes: ['online', 'offline'],
      terminalFields: [
        { key: 'integrated_bank_pos_server_ip', label: 'settingTable.server_ip' },
        { key: 'integrated_bank_pos_host', label: 'settingTable.host' },
        { key: 'integrated_bank_pos_port', label: 'settingTable.terminal_port' },
        { key: 'integrated_bank_pos_tid_mid', label: 'TID/MID' }
      ]
    }
  },
  methods: {
    fieldErrors (mode, field) {
      const index = this.entries(mode).findIndex(entry => entry.type === 'CARD_POS')
      const path = 'qr_payment_methods.' + mode + '.' + index + '.data.' + field
      return (this.errorBag && this.errorBag.items ? this.errorBag.items : [])
        .filter(item => item.field === path).map(item => item.msg)
    },
    modeErrors (mode) {
      const handled = this.mobileConnection(mode) === 'DIRECT'
        ? ['integrated_bank_pos_id', ...this.terminalFields.map(field => field.key)]
        : ['bank_terminal_provider']
      const index = this.entries(mode).findIndex(entry => entry.type === 'CARD_POS')
      const prefix = 'qr_payment_methods.' + mode
      return (this.errorBag && this.errorBag.items ? this.errorBag.items : [])
        .filter(item => item.field && (item.field === prefix || item.field.indexOf(prefix + '.') === 0 ||
          (mode === 'online' && item.field === 'qr_payment_methods')))
        .filter(item => !handled.some(field => item.field === prefix + '.' + index + '.data.' + field))
        .map(item => item.msg)
    },
    isMobileSelected (mode) {
      return this.isSelected(mode, 'CARD_POS')
    },
    toggleMobile (mode, selected) {
      this.toggleMethod(mode, 'CARD_POS', selected)
    },
    mobileConnection (mode) {
      const entry = this.entries(mode).find(entry => entry.type === 'CARD_POS')
      return entry ? ((entry.data && entry.data.connection_type) || 'NFC') : null
    },
    setMobileConnection (mode, connection) {
      const settings = this.copySettings()
      const entry = settings[mode].find(entry => entry.type === 'CARD_POS')
      if (!entry || this.mobileConnection(mode) === connection) return
      entry.data = connection === 'DIRECT'
        ? { connection_type: 'DIRECT', ...this.emptyTerminalData() }
        : { connection_type: 'NFC', bank_terminal_provider: this.legacyBankProvider }
      this.$emit('input', settings)
    },
    nfcProvider (mode) {
      const entry = this.entries(mode).find(entry => entry.type === 'CARD_POS')
      if (!entry) return null
      return entry.data ? entry.data.bank_terminal_provider : this.legacyBankProvider
    },
    setNfcProvider (mode, provider) {
      const settings = this.copySettings()
      const entry = settings[mode].find(entry => entry.type === 'CARD_POS')
      if (!entry) return
      entry.data = { connection_type: 'NFC', bank_terminal_provider: provider || null }
      this.$emit('input', settings)
    },
    entries (mode) {
      return this.value && Array.isArray(this.value[mode]) ? this.value[mode] : []
    },
    isSelected (mode, type) {
      return this.entries(mode).some(entry => entry.type === type)
    },
    emptyTerminalData () {
      return {
        integrated_bank_pos_id: null,
        integrated_bank_pos_name: null,
        integrated_bank_pos_server_ip: null,
        integrated_bank_pos_host: null,
        integrated_bank_pos_port: null,
        integrated_bank_pos_tid_mid: null
      }
    },
    terminalData (mode, type = 'CARD_POS') {
      const entry = this.entries(mode).find(entry => entry.type === type)
      return entry && entry.data ? entry.data : this.emptyTerminalData()
    },
    copySettings () {
      return {
        online: JSON.parse(JSON.stringify(this.entries('online'))).map(entry => entry.type === 'POS' ? {type: entry.type, data: null} : entry),
        offline: JSON.parse(JSON.stringify(this.entries('offline'))).map(entry => entry.type === 'POS' ? {type: entry.type, data: null} : entry)
      }
    },
    toggleMethod (mode, type, selected) {
      const settings = this.copySettings()
      settings[mode] = settings[mode].filter(entry => entry.type !== type)
      if (selected) {
        const data = type === 'CARD_POS' ? { connection_type: 'NFC', bank_terminal_provider: this.legacyBankProvider } : null
        settings[mode].push({ type, data })
      }
      this.$emit('input', settings)
    },
    setTerminalValue (mode, field, value, type = 'CARD_POS') {
      const settings = this.copySettings()
      const entry = settings[mode].find(entry => entry.type === type)
      if (!entry) return
      if (!entry.data) entry.data = this.emptyTerminalData()
      if (field === 'integrated_bank_pos_id') {
        const id = value === '' ? null : Number(value)
        const option = this.posOptions.find(option => option.id === id)
        entry.data.integrated_bank_pos_id = id
        entry.data.integrated_bank_pos_name = option ? option.name : null
      } else {
        entry.data[field] = value
      }
      this.$emit('input', settings)
    }
  }
}
</script>

<style scoped>
.qr-mobile-payment-options {
  margin-left: 24px;
  padding-left: 16px;
  border-left: 2px solid #e5e7eb;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Settings/TableManagement/QrPaymentSettings.vue